/**
 * useConnectionStatus Hook - DJ Live Network Reconnection and Recovery
 * Phase DL-4
 * 
 * Provides connection state management for DJ Live components with:
 * - Three states: connected, reconnecting, disconnected
 * - Automatic recovery with exponential backoff
 * - Data reconciliation after reconnection
 * - Last successful update timestamp tracking
 */

import { useState, useCallback, useRef, useEffect } from 'react';

// Connection states
export const CONNECTION_STATES = {
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  DISCONNECTED: 'disconnected'
};

// Configuration
const DEFAULT_CONFIG = {
  maxRetries: 3,                    // Failures before showing "reconnecting"
  disconnectedThreshold: 5,         // Failures before showing "disconnected"
  baseRetryDelay: 1000,             // Base delay for exponential backoff (ms)
  maxRetryDelay: 30000,             // Max delay between retries (ms)
  staleDataThreshold: 30000,        // Data older than this is considered stale (ms)
  recoveryGracePeriod: 2000,        // Time to wait after recovery before clearing indicator
};

/**
 * Hook for managing connection status in DJ Live components
 * 
 * @param {Object} options
 * @param {Function} options.fetchFn - Async function to fetch data
 * @param {Function} options.onDataReceived - Callback when new data is received
 * @param {Function} options.onReconcile - Callback to reconcile data after recovery
 * @param {number} options.pollInterval - Normal polling interval in ms
 * @param {Object} options.config - Override default configuration
 * 
 * @returns {Object} { connectionStatus, lastUpdate, isStale, fetch, resetConnection }
 */
export function useConnectionStatus({
  fetchFn,
  onDataReceived,
  onReconcile,
  pollInterval = 3000,
  config = {}
}) {
  const mergedConfig = { ...DEFAULT_CONFIG, ...config };
  
  const [connectionStatus, setConnectionStatus] = useState(CONNECTION_STATES.CONNECTED);
  const [lastSuccessfulUpdate, setLastSuccessfulUpdate] = useState(Date.now());
  const [isRecovering, setIsRecovering] = useState(false);
  
  // Refs for tracking state across renders
  const failureCountRef = useRef(0);
  const retryDelayRef = useRef(mergedConfig.baseRetryDelay);
  const lastDataRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const isFirstFetchRef = useRef(true);
  const wasDisconnectedRef = useRef(false);
  
  /**
   * Calculate if data is stale based on last successful update
   */
  const isStale = useCallback(() => {
    return Date.now() - lastSuccessfulUpdate > mergedConfig.staleDataThreshold;
  }, [lastSuccessfulUpdate, mergedConfig.staleDataThreshold]);
  
  /**
   * Handle successful fetch - reset counters, update status
   */
  const handleSuccess = useCallback((data) => {
    const wasDisconnected = wasDisconnectedRef.current;
    const previousData = lastDataRef.current;
    
    // Reset failure tracking
    failureCountRef.current = 0;
    retryDelayRef.current = mergedConfig.baseRetryDelay;
    setLastSuccessfulUpdate(Date.now());
    
    // If recovering from disconnection, reconcile data
    if (wasDisconnected && onReconcile && previousData) {
      setIsRecovering(true);
      onReconcile(previousData, data);
      
      // Show brief recovery indicator before returning to connected
      setTimeout(() => {
        setConnectionStatus(CONNECTION_STATES.CONNECTED);
        setIsRecovering(false);
        wasDisconnectedRef.current = false;
      }, mergedConfig.recoveryGracePeriod);
    } else {
      setConnectionStatus(CONNECTION_STATES.CONNECTED);
      wasDisconnectedRef.current = false;
    }
    
    // Store last known good data
    lastDataRef.current = data;
    
    // Notify caller of new data
    if (onDataReceived) {
      onDataReceived(data, isFirstFetchRef.current);
    }
    
    isFirstFetchRef.current = false;
  }, [mergedConfig.baseRetryDelay, mergedConfig.recoveryGracePeriod, onDataReceived, onReconcile]);
  
  /**
   * Handle fetch failure - increment counters, update status
   */
  const handleFailure = useCallback((error) => {
    failureCountRef.current += 1;
    const failures = failureCountRef.current;
    
    console.warn(`[ConnectionStatus] Fetch attempt ${failures} failed:`, error?.message || error);
    
    // Determine connection status based on failure count
    if (failures >= mergedConfig.disconnectedThreshold) {
      setConnectionStatus(CONNECTION_STATES.DISCONNECTED);
      wasDisconnectedRef.current = true;
      
      // Apply exponential backoff for retry delay
      retryDelayRef.current = Math.min(
        retryDelayRef.current * 2,
        mergedConfig.maxRetryDelay
      );
    } else if (failures >= mergedConfig.maxRetries) {
      setConnectionStatus(CONNECTION_STATES.RECONNECTING);
      wasDisconnectedRef.current = true;
    }
    // If failures < maxRetries, stay in CONNECTED state (transient error)
  }, [mergedConfig.disconnectedThreshold, mergedConfig.maxRetries, mergedConfig.maxRetryDelay]);
  
  /**
   * Execute a fetch with connection status tracking
   */
  const fetch = useCallback(async () => {
    try {
      const data = await fetchFn();
      handleSuccess(data);
      return { success: true, data };
    } catch (error) {
      handleFailure(error);
      return { success: false, error };
    }
  }, [fetchFn, handleSuccess, handleFailure]);
  
  /**
   * Reset connection state (useful for manual reconnection)
   */
  const resetConnection = useCallback(() => {
    failureCountRef.current = 0;
    retryDelayRef.current = mergedConfig.baseRetryDelay;
    wasDisconnectedRef.current = false;
    setConnectionStatus(CONNECTION_STATES.CONNECTED);
    setIsRecovering(false);
    fetch();
  }, [fetch, mergedConfig.baseRetryDelay]);
  
  /**
   * Get current retry delay for adaptive polling
   */
  const getCurrentRetryDelay = useCallback(() => {
    // Use normal poll interval when connected, backoff delay when disconnected
    if (connectionStatus === CONNECTION_STATES.CONNECTED) {
      return pollInterval;
    }
    return retryDelayRef.current;
  }, [connectionStatus, pollInterval]);
  
  /**
   * Set up adaptive polling with backoff
   */
  useEffect(() => {
    // Initial fetch
    fetch();
    
    // Set up polling with adaptive interval
    const setupPolling = () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      
      const delay = getCurrentRetryDelay();
      pollIntervalRef.current = setInterval(() => {
        fetch().then(() => {
          // Re-setup polling if interval should change
          const newDelay = getCurrentRetryDelay();
          if (newDelay !== delay) {
            setupPolling();
          }
        });
      }, delay);
    };
    
    setupPolling();
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetch, getCurrentRetryDelay]);
  
  return {
    connectionStatus,
    lastSuccessfulUpdate,
    isStale: isStale(),
    isRecovering,
    fetch,
    resetConnection,
    failureCount: failureCountRef.current,
    lastKnownData: lastDataRef.current
  };
}

export default useConnectionStatus;
